import React from "react";
import {observer} from "mobx-react";
import './modal.scss'
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import Utility from "../../Utility";
import {DATA_INTERVAL} from "../../MobxStores/DataStore";
import {WarningIcon} from "../../assets/svg-icons/WarningIcon";

const DeleteItemsModal = observer(() => {
    const {modalStore, generalStore, dragStore, projectStore, procedureStore, dataStore} = useStores();

    const closeModal = () => {
        modalStore.isDeleteItemsModalOpen = false;
        Utility.sleep(100).then(() => modalStore.resetModalData());
    }

    const renderBody = () =>
        <>
            {generalStore.translate('deleteAllSelectedItems')}? (
            {generalStore.translate('allFoldersAndProceduresInCurrentlySelectedFoldersWillAlsoBeDeleted')})
        </>

    const renderTitle = () => {
        return (
            <>
                <WarningIcon/>
                <div>{generalStore.translate('deleteItems')}</div>
            </>
        )
    }

    const deleteFolders = async (projectId, selectedFolders) => {
        const deleted = [0]; // array for thread safety
        const needToUpload = selectedFolders.length;
        await updateProgressLoader(deleted[0], needToUpload, 'folders');

        const promises = selectedFolders.map(folderId => projectStore.deleteFolderFromId(folderId)
            .then(async () => {
                deleted[0]++;
                await updateProgressLoader(deleted[0], needToUpload, 'folders');
            })
        );
        await Promise.all(promises);
        await updateProgressLoader(0, 0, 'folders', true);
    }

    const deleteProcedures = async (projectId, projectProceduresMap, selectedProcedures) => {
        const deleted = [0]; // array for thread safety
        const needToUpload = selectedProcedures.length;
        await updateProgressLoader(deleted[0], needToUpload, 'procedures');

        const promises = selectedProcedures.map(procedureId => {
            const procedure = projectProceduresMap.get(procedureId);
            return procedureStore.deleteProcedure(procedure)
                .then(async newProcedure => {
                    deleted[0]++;
                    await updateProgressLoader(deleted[0], needToUpload, 'procedures');
                });
        });
        await Promise.all(promises);
        await updateProgressLoader(0, 0, 'procedures', true);
    }

    const updateProgressLoader = async (uploaded, needToUpload, itemsName, shouldClose = false) => {
        if (shouldClose) {
            generalStore.progressLoaderValue = null;
            return;
        }
        generalStore.progressLoaderValue = 100 * (uploaded / needToUpload);
        generalStore.progressLoaderString = `${generalStore.translate('deleting')}: ${uploaded} ${generalStore.translate('of', true)} ${needToUpload} ${generalStore.translate(itemsName, true)}`;
        await Utility.sleep(200); // making it visible to the human eye
    }

    const onConfirm = async () => {
        const selectedFoldersSet = dragStore.selectedFoldersIds;
        const selectedProceduresSet = dragStore.selectedProceduresIds;

        const projectId = projectStore.currentProject?.ProjectIdentifier;
        const projectProceduresMap = dataStore.projectsProceduresMap.get(projectId);
        const projectFoldersMap = dataStore.projectsFoldersMap.get(projectId);

        if (!!selectedFoldersSet.size) {
            await deleteFolders(projectId, [...selectedFoldersSet.values()]);
        }

        if (!!selectedProceduresSet.size) {
            await deleteProcedures(projectId, projectProceduresMap, [...selectedProceduresSet.values()]);
        }

        generalStore.isLoaderOpen = true;

        selectedFoldersSet.forEach(folderId => projectFoldersMap.delete(folderId));
        selectedProceduresSet.forEach(procedureId => projectProceduresMap.delete(procedureId));

        dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
        dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
        dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);

        await projectStore.refreshProjectDetails();

        modalStore.modalData.updateParent();
        dragStore.resetDragMedia();
        generalStore.isLoaderOpen = false;
        closeModal();
    }

    return (
        <GeneralDialog
            openControl='isDeleteItemsModalOpen'
            className='open-style-dialog delete-open-style'
            onEnter={null}
            title={renderTitle()}
            body={renderBody()}
            isConfirmDisabled={false}
            onConfirm={onConfirm}
            confirmText={generalStore.translate('delete')}
            buttonClass='warning'
            specialOnClose={closeModal}
        />
    );
});

export default DeleteItemsModal;
